import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Button,
  TextField,
  Alert,
  Paper,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  Payment as BkashIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import axios from 'axios';

interface PaymentMethodSelectorProps {
  amount?: number;
  onPaymentMethodSelect: (method: string, data?: any) => void;
  loading?: boolean;
  error?: string;
  formData?: any; // Add form data prop
  formId?: string; // Add form ID prop
  customUrl?: string; // Add custom URL prop
}

interface BankTransferSettings {
  bank_transfer_account_name: string;
  bank_transfer_account_number: string;
  bank_transfer_bank_name: string;
  bank_transfer_routing_number: string;
  bank_transfer_instructions: string;
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  amount = 0,
  onPaymentMethodSelect,
  loading = false,
  error,
  formData,
  formId,
  customUrl
}) => {
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [paymentReceipt, setPaymentReceipt] = useState<File | null>(null);
  const [bankTransferNotes, setBankTransferNotes] = useState('');
  const [bkashLoading, setBkashLoading] = useState(false);
  const [bkashError, setBkashError] = useState<string | null>(null);
  const [bankTransferSettings, setBankTransferSettings] = useState<BankTransferSettings>({
    bank_transfer_account_name: '',
    bank_transfer_account_number: '',
    bank_transfer_bank_name: '',
    bank_transfer_routing_number: '0',
    bank_transfer_instructions: ''
  });
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Load bank transfer settings on component mount
  useEffect(() => {
    const loadBankTransferSettings = async () => {
      try {
        setSettingsLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/payment`);
        
        if (response.data.success && response.data.settings) {
          setBankTransferSettings(prevSettings => ({
            ...prevSettings,
            ...response.data.settings
          }));
        }
      } catch (error) {
        console.error('Failed to load bank transfer settings:', error);
        // Keep default settings if loading fails
      } finally {
        setSettingsLoading(false);
      }
    };

    loadBankTransferSettings();
  }, []);

  const hasBankTransfer = Boolean(
    bankTransferSettings.bank_transfer_account_name &&
    bankTransferSettings.bank_transfer_account_number &&
    bankTransferSettings.bank_transfer_account_number !== '0'
  );

  // If bank transfer becomes unavailable while selected, clear selection
  useEffect(() => {
    if (!hasBankTransfer && selectedMethod === 'bank_transfer') {
      setSelectedMethod('');
    }
  }, [hasBankTransfer, selectedMethod]);

  const handleMethodSelect = (method: string) => {
    setSelectedMethod(method);
  };

  const handleBkashPayment = async () => {
    setBkashLoading(true);
    setBkashError(null);
    
    try {
      // Step 1: Create bKash payment
      const orderId = `ORDER_${Date.now()}`;
      const paymentResponse = await axios.post(`${process.env.REACT_APP_API_URL}/api/bkash/create`, {
        amount: amount,
        orderId: orderId,
        intent: 'sale'
      });

      if (!paymentResponse.data.success || !paymentResponse.data.bkashURL) {
        setBkashError(paymentResponse.data.message || 'Failed to create bKash payment');
        return;
      }

      const paymentID = paymentResponse.data.paymentID;

      // Step 2: Save form data to database with pending payment status
      const submissionFormData = new FormData();
      submissionFormData.append('formId', formId || '');
      
      // Add all form fields
      if (formData) {
        Object.keys(formData).forEach(fieldKey => {
          const value = formData[fieldKey];
          if (value !== null && value !== undefined) {
            if (Array.isArray(value)) {
              submissionFormData.append(fieldKey, JSON.stringify(value));
            } else {
              submissionFormData.append(fieldKey, String(value));
            }
          }
        });
      }
      
      // Add payment information
      submissionFormData.append('paymentMethod', 'bkash');
      submissionFormData.append('bkashPaymentId', paymentID);
      submissionFormData.append('orderId', orderId);
      submissionFormData.append('paymentStatus', 'pending'); // Mark as pending
      submissionFormData.append('paymentAmount', amount.toString()); // Add the payment amount

      console.error("sd", submissionFormData);

      // Save the submission
      const submissionResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/submissions`,
        submissionFormData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Step 3: Store submission details for later update
      sessionStorage.setItem('bkashPaymentSession', JSON.stringify({
        paymentID: paymentID,
        submissionId: submissionResponse.data.submissionId,
        uniqueId: submissionResponse.data.uniqueId,
        editCode: submissionResponse.data.editCode,
        amount: amount,
        timestamp: Date.now(),
        formData: formData, // Store form data as backup
        customUrl: customUrl // Store custom URL for navigation
      }));

      // Step 4: Redirect to bKash payment URL
      console.log('Redirecting to bKash payment URL:', paymentResponse.data.bkashURL);
      window.location.href = paymentResponse.data.bkashURL;

    } catch (error: any) {
      console.error('bKash payment error:', error);
      let errorMessage = 'Failed to initiate bKash payment';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 401) {
        errorMessage = 'bKash authentication failed. Please try again.';
      } else if (error.response?.status === 500) {
        errorMessage = 'bKash service is temporarily unavailable. Please try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setBkashError(errorMessage);
    } finally {
      setBkashLoading(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please upload only JPG, PNG, or PDF files');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      
      setPaymentReceipt(file);
    }
  };

  const handleProceedPayment = () => {
    if (!selectedMethod) {
      alert('Please select a payment method');
      return;
    }

    if (selectedMethod === 'bkash') {
      handleBkashPayment();
    } else if (selectedMethod === 'bank_transfer') {
      if (!paymentReceipt) {
        alert('Please upload payment receipt');
        return;
      }
      onPaymentMethodSelect('bank_transfer', { 
        receipt: paymentReceipt, 
        notes: bankTransferNotes.trim() 
      });
    } else {
      onPaymentMethodSelect(selectedMethod);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, maxWidth: 700, mx: 'auto', mb: 4 }}>
      <Typography variant="h5" component="h2" gutterBottom align="center" sx={{ fontWeight: 600, mb: 3 }}>
        Select Payment Method
      </Typography>
      
      <Typography variant="h6" align="center" color="primary" gutterBottom sx={{ mb: 4 }}>
        Amount: ৳{amount.toFixed(2)}
      </Typography>

      <Divider sx={{ my: 4 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Payment Error
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
          {error.includes('bKash') && (
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              You can try Bank Transfer as an alternative payment method.
            </Typography>
          )}
        </Alert>
      )}

      <Box display="flex" gap={3} flexDirection={{ xs: 'column', sm: 'row' }}>
        {/* Bank Transfer (show only if configured) */}
        {hasBankTransfer && (
          <Box flex={1}>
            <Card 
              variant={selectedMethod === 'bank_transfer' ? 'outlined' : 'elevation'}
              sx={{ 
                height: '100%',
                border: selectedMethod === 'bank_transfer' ? 2 : 0,
                borderColor: selectedMethod === 'bank_transfer' ? 'primary.main' : 'transparent'
              }}
            >
              <CardActionArea 
                onClick={() => handleMethodSelect('bank_transfer')}
                sx={{ height: '100%', p: 2 }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <BankIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Bank Transfer
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Transfer to our bank account and upload receipt
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Box>
        )}

        {/* Bkash */}
        <Box flex={1}>
          <Card 
            variant={selectedMethod === 'bkash' ? 'outlined' : 'elevation'}
            sx={{ 
              height: '100%',
              border: selectedMethod === 'bkash' ? 2 : 0,
              borderColor: selectedMethod === 'bkash' ? 'primary.main' : 'transparent'
            }}
          >
            <CardActionArea 
              onClick={() => handleMethodSelect('bkash')}
              sx={{ height: '100%', p: 2 }}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <BkashIcon sx={{ fontSize: 48, color: '#e2136e', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Bkash
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Pay using Bkash mobile banking
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Box>
      </Box>

      {/* Bank Transfer Details */}
      {selectedMethod === 'bank_transfer' && (
        <Box 
          mt={6} 
          sx={{ 
            backgroundColor: '#f8f9fa', 
            p: 4, 
            borderRadius: 3,
            border: '1px solid #e9ecef',
            position: 'relative',
            overflow: 'visible'
          }}
        >
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Note
            </Typography>
            <Typography variant="body2">
              Manual verification required - <strong>may take time</strong> for approval. You will be notified once payment is verified.
            </Typography>
          </Alert>
          <Alert severity="info" sx={{ mb: 4 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Bank Transfer Instructions:
            </Typography>
            <Box component="div" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Account Name:</strong> {bankTransferSettings.bank_transfer_account_name || 'Not configured'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Account Number:</strong> {bankTransferSettings.bank_transfer_account_number || 'Not configured'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Bank:</strong> {bankTransferSettings.bank_transfer_bank_name || 'Not configured'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Routing Number:</strong> {bankTransferSettings.bank_transfer_routing_number || 'Not configured'}
              </Typography>
              {bankTransferSettings.bank_transfer_instructions && (
                <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                  <strong>Additional Instructions:</strong><br />
                  {bankTransferSettings.bank_transfer_instructions}
                </Typography>
              )}
            </Box>
            {settingsLoading && (
              <Typography variant="caption" color="textSecondary" display="block" mt={2}>
                Loading bank details...
              </Typography>
            )}
          </Alert>

          <TextField
            fullWidth
            label="Additional Notes (Optional)"
            value={bankTransferNotes}
            onChange={(e) => setBankTransferNotes(e.target.value)}
            multiline
            rows={3}
            margin="normal"
            sx={{ mb: 4 }}
          />

          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography 
              variant="subtitle1" 
              gutterBottom 
              sx={{ 
                fontWeight: 600, 
                mb: 3,
                color: 'text.primary',
                fontSize: '1.1rem'
              }}
            >
              Upload Payment Receipt *
            </Typography>
            <Box 
              sx={{ 
                border: '2px dashed #ccc',
                borderRadius: 3,
                p: 5,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backgroundColor: paymentReceipt ? '#e8f5e8' : 'white',
                minHeight: '180px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': { 
                  borderColor: 'primary.main',
                  backgroundColor: paymentReceipt ? '#e8f5e8' : '#f5f5f5',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }
              }}
              component="label"
            >
              <input
                type="file"
                hidden
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={handleFileUpload}
              />
              <UploadIcon 
                sx={{ 
                  fontSize: 64, 
                  color: paymentReceipt ? 'success.main' : 'text.secondary', 
                  mb: 2,
                  transition: 'all 0.3s ease'
                }} 
              />
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                  fontWeight: 500,
                  maxWidth: '300px',
                  wordBreak: 'break-word',
                  lineHeight: 1.3
                }}
              >
                {paymentReceipt ? paymentReceipt.name : 'Click to upload receipt'}
              </Typography>
              <Typography 
                variant="body2" 
                color="textSecondary" 
                sx={{ 
                  mb: paymentReceipt ? 2 : 0,
                  opacity: 0.8
                }}
              >
                JPG, PNG, or PDF (max 5MB)
              </Typography>
              {paymentReceipt && (
                <Typography 
                  variant="body2" 
                  color="success.main" 
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 600,
                    gap: 1
                  }}
                >
                  ✓ File uploaded successfully
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Bkash Details */}
      {selectedMethod === 'bkash' && (
        <Box mt={5} sx={{ backgroundColor: '#fdf4f6', p: 3, borderRadius: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              bKash Payment Process:
            </Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              1. Click "Pay with bKash" button below<br />
              2. Complete payment in the bKash popup window<br />
              3. Return to this page to confirm payment<br />
              <strong>Amount: ৳{amount ? amount.toFixed(2) : '0.00'}</strong>
            </Typography>
          </Alert>

          {bkashError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {bkashError}
            </Alert>
          )}

          <Box textAlign="center">
            <Button
              variant="contained"
              size="large"
              onClick={handleBkashPayment}
              disabled={bkashLoading}
              sx={{ 
                minWidth: 220,
                py: 1.5,
                backgroundColor: '#e2136e',
                '&:hover': { backgroundColor: '#c10d5a' },
                '&:disabled': { backgroundColor: '#ccc' }
              }}
            >
              {bkashLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                  Processing...
                </>
              ) : (
                'Pay with bKash'
              )}
            </Button>
          </Box>
        </Box>
      )}

      {/* Proceed Button */}
      {selectedMethod && selectedMethod !== 'bkash' && (
        <Box mt={5} textAlign="center" sx={{ pt: 2 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleProceedPayment}
            disabled={loading}
            sx={{ 
              minWidth: 220,
              py: 1.5,
              fontSize: '1.1rem'
            }}
          >
            {loading ? 'Processing...' : 'Proceed with Bank Transfer'}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default PaymentMethodSelector;
